import React, { useState, useCallback } from "react";
import fp from "lodash/fp";
import moment from "moment";
import { Container } from "react-bootstrap";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";

import { Skeleton } from "components";
import { getErrorMessage } from "helpers";
import { useLoader } from "hooks";
import { autoChecklistService } from "services";
import { uiActions } from "store";
import { ErrorResponse } from "types";
import {
	commaAndFloat2Normalizer,
	floatWithComma2Normalizer,
} from "validators";
import {
	maxValue100,
	onlyDate,
	onlyDigits,
	onlyPercent,
	rangeMinMaxValueForNumber300and850,
} from "validators/validation";

import ExperianFieldForm from "./experianFieldForm";

import styles from "./../autoCheckList.module.scss";

type ExperianInfoProps = {
	experianData: { [key: string]: any };
	isLoading: boolean;
	isEditExperianFields: boolean;
	ownerType: string;
	applicationId: string;
	refetchExperianInfo: Function;
};

type updateExperianValuesProps = {
	values: { [key: string]: any };
	ownerType: string;
};

const ExperianInfo = ({
	isLoading,
	experianData,
	ownerType,
	applicationId,
	refetchExperianInfo,
	isEditExperianFields,
}: ExperianInfoProps) => {
	const dispatch = useDispatch();
	const [isClosed, setIsClosed] = useState(true);

	const Loader = useLoader(isLoading);

	const {
		mutateAsync: mutateExperianInfo,
		isLoading: loadingUpdateExperianInfo,
	} = useMutation(
		({ values, ownerType }: updateExperianValuesProps) => {
			const model = {
				bankruptcy_date: fp.isEmpty(values?.bankruptcy_date)
					? experianData?.bankruptcy_date
					: values.bankruptcy_date,
				bankruptcy_status: fp.isEmpty(values?.bankruptcy_status)
					? experianData?.bankruptcy_status
					: values.bankruptcy_status,
				fico_score: fp.isEmpty(values.fico_score)
					? experianData?.fico_score
					: values.fico_score,
				open_accounts_count: fp.isEmpty(values?.open_accounts_count)
					? experianData?.open_accounts_count
					: values?.open_accounts_count,
				revolving_credit_available: fp.isEmpty(
					values?.revolving_credit_available,
				)
					? experianData?.revolving_credit_available
					: values?.revolving_credit_available,
				delinquent_accounts_count: fp.isEmpty(
					values?.delinquent_accounts_count,
				)
					? experianData?.delinquent_accounts_count
					: values?.delinquent_accounts_count,
				real_estate_balance: fp.isEmpty(values?.real_estate_balance)
					? experianData?.real_estate_balance
					: values?.real_estate_balance,
			};
			return autoChecklistService.updateExperianValues(
				applicationId,
				ownerType,
				model,
			);
		},
		{
			onSuccess() {
				refetchExperianInfo();
				dispatch(
					uiActions.showSuccessNotification({
						title: "Experian information has been successfully updated.",
					}),
				);
			},
			onError(error: ErrorResponse) {
				dispatch(
					uiActions.showErrorNotification({
						title: getErrorMessage(error),
					}),
				);
			},
		},
	);

	const handleSubmit = useCallback(
		(values, ownerType, hideEditField: Function) => {
			mutateExperianInfo({ values, ownerType }).then(() => {
				hideEditField();
			});
		},
		[mutateExperianInfo],
	);
	return (
		<Loader
			skeleton={<Skeleton variant="text" height="50px" />}
			component={
				<Container
					className={`${styles.experianInfoContainer} ${
						isClosed && styles.closed
					}`}
				>
					<div
						className={`${styles.arrow} ${
							isClosed && styles.closed
						}`}
						onClick={() => {
							setIsClosed(!isClosed);
						}}
					/>
					{experianData?.bankruptcy_status && (
						<>
							<ExperianFieldForm
								label="Bankruptcy Date"
								value={experianData?.bankruptcy_date}
								dataFormat={data =>
									moment(data).format("MM/DD/YYYY")
								}
								fieldName="bankruptcy_date"
								normalize={onlyDate}
								ownerType={ownerType}
								isLoading={
									loadingUpdateExperianInfo || isLoading
								}
								updateExperianField={handleSubmit}
								isEditExperianFields={isEditExperianFields}
							/>
							<ExperianFieldForm
								label="Bankruptcy Status"
								value={experianData?.bankruptcy_status}
								fieldName="bankruptcy_status"
								ownerType={ownerType}
								isLoading={
									loadingUpdateExperianInfo || isLoading
								}
								updateExperianField={handleSubmit}
								isEditExperianFields={isEditExperianFields}
							/>
						</>
					)}
					<ExperianFieldForm
						label="FICO Score"
						value={experianData?.fico_score}
						fieldName="fico_score"
						validate={rangeMinMaxValueForNumber300and850}
						ownerType={ownerType}
						normalize={onlyDigits}
						isLoading={loadingUpdateExperianInfo || isLoading}
						updateExperianField={handleSubmit}
						isEditExperianFields={isEditExperianFields}
					/>
					<ExperianFieldForm
						label="Number of Open Accounts"
						value={experianData?.open_accounts_count}
						normalize={onlyDigits}
						fieldName="open_accounts_count"
						ownerType={ownerType}
						isLoading={loadingUpdateExperianInfo || isLoading}
						updateExperianField={handleSubmit}
						isEditExperianFields={isEditExperianFields}
					/>
					<ExperianFieldForm
						label="Revolving Credit Available"
						value={experianData?.revolving_credit_available}
						dataFormat={(data, isEdit) =>
							fp.isNumber(data) && `${data}${isEdit ? "" : "%"}`
						}
						fieldName="revolving_credit_available"
						ownerType={ownerType}
						validate={maxValue100}
						normalize={onlyPercent}
						isLoading={loadingUpdateExperianInfo || isLoading}
						updateExperianField={handleSubmit}
						isEditExperianFields={isEditExperianFields}
					/>
					<ExperianFieldForm
						label="Delinquent Accounts"
						value={experianData?.delinquent_accounts_count}
						fieldName="delinquent_accounts_count"
						normalize={onlyDigits}
						ownerType={ownerType}
						isLoading={loadingUpdateExperianInfo || isLoading}
						updateExperianField={handleSubmit}
						isEditExperianFields={isEditExperianFields}
					/>
					<ExperianFieldForm
						label="Real Estate Balance"
						value={experianData?.real_estate_balance}
						normalizeOnBlur={floatWithComma2Normalizer}
						normalize={commaAndFloat2Normalizer}
						dataFormat={(data, isEdit) =>
							fp.isNumber(data) &&
							`${isEdit ? "" : "$"}${floatWithComma2Normalizer(
								data,
							)}`
						}
						fieldName="real_estate_balance"
						ownerType={ownerType}
						isLoading={loadingUpdateExperianInfo || isLoading}
						updateExperianField={handleSubmit}
						isEditExperianFields={isEditExperianFields}
					/>
				</Container>
			}
		/>
	);
};

export default ExperianInfo;
